import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Search, Download, Mail } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

const STATUS_COLORS = { draft: 'bg-muted text-foreground', approved: 'bg-success/20 text-success', cancelled: 'bg-error/20 text-error' };

export default function BillReturnsPage() {
  const [searchParams] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [search, setSearch] = useState('');
  const [emailDialog, setEmailDialog] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [form, setForm] = useState({ supplier_id: '', supplier_name: '', bill_id: '', invoice_number: '', date: localToday(), reason: '', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] });

  useEffect(() => { fetchData(); }, []);

  // Deep-link: when BillsPage sends us `?bill_id=X&supplier_id=Y`, auto-
  // open the "Create Bill Return" form pre-filled with that supplier &
  // bill reference so the user lands on the right form in one click.
  useEffect(() => {
    const billId = searchParams.get('bill_id');
    const supplierId = searchParams.get('supplier_id');
    if (!billId && !supplierId) return;
    // Wait until suppliers list is loaded so we can resolve the name.
    if (suppliers.length === 0) return;
    const s = supplierId ? suppliers.find((x) => x.id === supplierId) : null;
    setEditDoc(null);
    setForm((f) => ({
      ...f,
      supplier_id: supplierId || f.supplier_id,
      supplier_name: s?.name || f.supplier_name,
      bill_id: billId || f.bill_id,
    }));
    setShowForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, searchParams]);
  const fetchData = async () => {
    try {
      const [r, s] = await Promise.all([api.get('/bill-returns'), api.get('/suppliers')]);
      setDocs(r.data || []); setSuppliers(s.data?.data || s.data || []);
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  };

  const calcTotal = (items) => items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0);

  const save = async () => {
    const items = form.items.filter(i => i.description);
    const subtotal = calcTotal(items);
    try {
      if (editDoc) await api.put(`/bill-returns/${editDoc.id}`, { ...form, items, subtotal, total: subtotal });
      else await api.post('/bill-returns', { ...form, items, subtotal, total: subtotal });
      toast.success(editDoc ? 'Updated' : 'Created'); setShowForm(false); setEditDoc(null); fetchData();
    } catch { toast.error('Save failed'); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/bill-returns/${id}`); toast.success('Deleted'); fetchData(); };
  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/bill-returns/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `bill_return_${id.slice(0, 8)}.pdf`; a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
  };
  const sendEmail = async () => {
    if (!emailTo || !emailDialog) return;
    try {
      const r = await api.post(`/bill-returns/${emailDialog}/email`, { email: emailTo });
      if (r.data?.success) { toast.success(r.data.message); setEmailDialog(null); setEmailTo(''); }
      else toast.error(r.data?.error || 'Send failed');
    } catch { toast.error('Email send failed'); }
  };
  const openNew = () => { setEditDoc(null); setForm({ supplier_id: '', supplier_name: '', bill_id: '', invoice_number: '', date: localToday(), reason: '', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] }); setShowForm(true); };
  const openEdit = (d) => { setEditDoc(d); setForm({ supplier_id: d.supplier_id, supplier_name: d.supplier_name, bill_id: d.bill_id || '', invoice_number: d.invoice_number || '', date: d.date, reason: d.reason || '', notes: d.notes || '', items: d.items?.length ? d.items : [{ description: '', quantity: 1, unit_price: 0 }] }); setShowForm(true); };

  const filtered = docs.filter(d => !search || d.reference_id?.toLowerCase().includes(search.toLowerCase()) || d.supplier_name?.toLowerCase().includes(search.toLowerCase()) || d.invoice_number?.toLowerCase().includes(search.toLowerCase()));

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="bill-returns-page">
        <div className="flex items-center justify-between">
          <div><h1 className="text-xl font-bold text-foreground">Bill Returns</h1><p className="text-sm text-muted-foreground">Return goods to suppliers and record adjustments</p></div>
          <Button onClick={openNew} data-testid="create-bill-return-btn"><Plus size={16} className="mr-1" /> Create Bill Return</Button>
        </div>
        <div className="relative max-w-xs"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" /></div>
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b"><tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reference</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Invoice No.</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Supplier</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reason</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No bill returns yet</td></tr> :
                 filtered.map(d => (
                  <tr key={d.id} className="border-b hover:bg-muted/50">
                    <td className="px-4 py-3 font-mono text-xs font-medium">{d.reference_id}</td>
                    <td className="px-4 py-3 text-xs">{d.invoice_number ? <span className="font-mono text-info bg-info/10 px-1.5 py-0.5 rounded">{d.invoice_number}</span> : <span className="text-muted-foreground">-</span>}</td>
                    <td className="px-4 py-3">{d.supplier_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.date}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{d.reason}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_COLORS[d.status] || 'bg-muted'}>{d.status}</Badge></td>
                    <td className="px-4 py-3 text-right font-medium">{(d.total || 0).toLocaleString('en', { minimumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(d)} className="text-xs h-7">Edit</Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(d.id)} className="text-xs h-7 text-orange-600" data-testid={`bill-return-pdf-${d.id}`}><Download size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEmailDialog(d.id); setEmailTo(''); }} className="text-xs h-7 text-info" data-testid={`bill-return-email-${d.id}`}><Mail size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(d.id)} className="text-xs h-7 text-error"><Trash2 size={12} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editDoc ? 'Edit' : 'New'} Bill Return</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Supplier</label>
                <Select value={form.supplier_id} onValueChange={v => { const s = suppliers.find(x => x.id === v); setForm({...form, supplier_id: v, supplier_name: s?.name || ''}); }}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Date</label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Invoice No. (Optional)</label><Input value={form.invoice_number} onChange={e => setForm({...form, invoice_number: e.target.value})} placeholder="e.g. INV-2024-001" data-testid="bill-return-invoice-number" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Reason</label><Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Reason for return" /></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-2 block">Line Items</label>
              {form.items.map((item, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input placeholder="Description" value={item.description} onChange={e => { const items = [...form.items]; items[i].description = e.target.value; setForm({...form, items}); }} className="flex-1" />
                  <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => { const items = [...form.items]; items[i].quantity = e.target.value; setForm({...form, items}); }} className="w-20" />
                  <Input type="number" placeholder="Price" value={item.unit_price} onChange={e => { const items = [...form.items]; items[i].unit_price = e.target.value; setForm({...form, items}); }} className="w-28" />
                  <Button variant="ghost" size="sm" onClick={() => { const items = form.items.filter((_, j) => j !== i); setForm({...form, items: items.length ? items : [{ description: '', quantity: 1, unit_price: 0 }]}); }}><Trash2 size={14} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setForm({...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }]})} className="text-xs">+ Add Line</Button></div>
            <div className="text-right text-lg font-bold">Total: {calcTotal(form.items).toLocaleString('en', { minimumFractionDigits: 2 })}</div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Notes</label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes" /></div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button><Button onClick={save} data-testid="save-bill-return-btn">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!emailDialog} onOpenChange={() => setEmailDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Send via Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="recipient@email.com" type="email" data-testid="email-input" />
            <p className="text-xs text-muted-foreground">PDF will be attached to the email</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailDialog(null)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={!emailTo} data-testid="send-email-btn"><Mail size={14} className="mr-1" /> Send</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
